import type { FormViewT, InputT } from "@patient-watch/types";

/** The earlier answer to a carry-forward input. */
export type CarriedAnswerT = string | number | boolean | string[];

export type CarriedInputT = InputT & { carried_value?: CarriedAnswerT };

/**
 * Puts the diary's earlier answer on each carry-forward input that has one, as `carried_value`. The form asks the
 * question as usual, filled in with that answer, and says it was carried forward from last time.
 * Returns the same form when nothing is carried.
 */
export function markCarriedInputs(form: FormViewT, carried: ReadonlyMap<string, CarriedAnswerT>): FormViewT {
    if (carried.size === 0) return form;
    return {
        ...form,
        pages: form.pages?.map((page) => ({
            ...page,
            inputs: page.inputs?.map((input) =>
                carried.has(input.input_id) ? { ...input, carried_value: carried.get(input.input_id) } : input
            ),
        })),
    };
}

/** The carried answers as entry-value rows, the shape the form defaults are built from. */
export function carriedEntryValuesOf(inputs: readonly CarriedInputT[]) {
    return inputs.flatMap((input) => {
        const value = input.carried_value;
        if (value == null) return [];
        return [
            {
                input_id: input.input_id,
                number_value: typeof value === "number" ? value : null,
                text_value: typeof value === "string" ? value : null,
                text_array: Array.isArray(value) ? value : null,
            },
        ];
    });
}
